import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as apicall from "../../components/Apicall";
import * as companyService from "../../services/companyCustomerService";
import CompaniesTab from "./CompaniesTab";

const PAGE_SIZE = 10;
// Backend "name" sort just does ORDER BY first_name ASC, which sorts by raw
// collation (special chars/digits before letters). Fetch everything and sort
// client-side instead so alphabetic names always come first.
const NAME_SORT_FETCH_LIMIT = 100000;

const FILTERS = [
  { key: "all", label: "All Customers" },
  { key: "high_value", label: "High Value" },
];

const SORT_OPTIONS = [
  { key: "recent", label: "Recent" },
  { key: "name", label: "Name" },
  { key: "spent", label: "Total Spent" },
];

const STATUS_COLORS = {
  Active: { bg: "#dcfce7", text: "#16a34a" },
  Inactive: { bg: "#fee2e2", text: "#dc2626" },
};

function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function fullName(c) {
  return [c?.first_name, c?.last_name].filter(Boolean).join(" ") || "—";
}

function startsWithLetter(str) {
  return /^\p{L}/u.test(str || "");
}

// Alphabetic names first (case-insensitive, locale-aware), names starting
// with digits/symbols/punctuation after.
function compareCustomersByName(a, b) {
  const an = fullName(a) === "—" ? "" : fullName(a).trim();
  const bn = fullName(b) === "—" ? "" : fullName(b).trim();
  const aLetter = startsWithLetter(an);
  const bLetter = startsWithLetter(bn);
  if (aLetter !== bLetter) return aLetter ? -1 : 1;
  return an.localeCompare(bn, undefined, { sensitivity: "base" });
}

function formatDateTime(dateStr, timeStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const label = d.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
  if (!timeStr) return label;
  const [h, m] = timeStr.split(":");
  if (h === undefined) return label;
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${label} · ${h12}:${m} ${period}`;
}

export default function Customers() {
  const navigate = useNavigate();
  // Individuals and companies are two lists on one screen. The tab lives in the address
  // (?tab=companies) so Back from a company returns to the Companies tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "companies" ? "companies" : "individuals";
  const setTab = (next) => setSearchParams(next === "companies" ? { tab: "companies" } : {}, { replace: true });
  const [companySearch, setCompanySearch] = useState("");
  const [companyTotal, setCompanyTotal] = useState(null);
  const [individualTotal, setIndividualTotal] = useState(null);

  // const userDetails = (() => {
  //   try {
  //     const d = localStorage.getItem("userDetails");
  //     return d && d !== "undefined" ? JSON.parse(d) : null;
  //   } catch {
  //     return null;
  //   }
  // })();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [industry, setIndustry] = useState(null);

  // The companies count for the tab, before the Companies tab has been opened.
  useEffect(() => {
    companyService
      .listCompanies({ limit: 1 })
      .then((res) => res?.pagination && setCompanyTotal(res.pagination.total))
      .catch(() => {});
  }, []);

     useEffect(() => {
      const fetchProfile = async () => {
        try {
          const response = await apicall.get("ezpMyProfile");
          const profile = response?.data?.[0];
          if (profile) {
            setIndustry(profile);
          }
        } catch (e) {
          console.error("Failed to fetch profile for currency:", e);
        }
      };
      fetchProfile();
    }, []);
   const currency = industry?.currency || "AED";
  const fmtMoney = (n) => `${currency} ${Number(n || 0).toLocaleString("en", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;
  const CUSTOMER_TERM = isClinicService ? "Patient" : isSportsService ? "Player" : "Customer";
  const CUSTOMER_TERM_PLURAL = isClinicService ? "Patients" : isSportsService ? "Players" : "Customers";
  
  const fetchCustomers = useCallback(async (pageNum, searchVal, filterVal, sortVal) => {
    try {
      setLoading(true);
      if (sortVal === "name") {
        // Fetch everything matching the current filter/search and sort
        // client-side — see compareCustomersByName for why.
        const params = new URLSearchParams({ page: 1, limit: NAME_SORT_FETCH_LIMIT, filter: filterVal, sortBy: sortVal });
        if (searchVal) params.set("search", searchVal);
        const res = await apicall.get(`ezp_customers?${params.toString()}`);
        if (res?.success) {
          const sorted = [...(res.data || [])].sort(compareCustomersByName);
          const total = sorted.length;
          const start = (pageNum - 1) * PAGE_SIZE;
          setCustomers(sorted.slice(start, start + PAGE_SIZE));
          setPagination({ page: pageNum, limit: PAGE_SIZE, total, totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)) });
          if (filterVal === "all" && !searchVal) setIndividualTotal(total);
        }
      } else {
        const params = new URLSearchParams({ page: pageNum, limit: PAGE_SIZE, filter: filterVal, sortBy: sortVal });
        if (searchVal) params.set("search", searchVal);
        const res = await apicall.get(`ezp_customers?${params.toString()}`);
        if (res?.success) {
          setCustomers(res.data || []);
          setPagination(res.pagination || null);
          if (filterVal === "all" && !searchVal && res.pagination) setIndividualTotal(res.pagination.total);
        }
      }
    } catch (err) {
      console.error("fetchCustomers error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers(page, search, filter, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter, sortBy, fetchCustomers]);

  // Debounced search — resets to page 1 on every keystroke pause
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCustomers(1, search, filter, sortBy);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleFilterChange = (key) => {
    setFilter(key);
    setPage(1);
  };

  const handleSortChange = (key) => {
    setSortBy(key);
    setSortOpen(false);
    setPage(1);
  };

  const openCustomer = (c) => {
    const base = c.record_type === "dependent" ? `/customers/profile/dependent/${c.id}` : `/customers/profile/${c.id}`;
    navigate(base, { state: { customerData: c ,industry: industry } });
  };

  const totalPages = pagination?.totalPages ?? 1;

  const openCompany = (c) => navigate(`/customers/company/${c.id}`, { state: { companyData: c } });
  const onCompanies = tab === "companies";
  const searchValue = onCompanies ? companySearch : search;
  const setSearchValue = onCompanies ? setCompanySearch : setSearch;
  const TABS = [
    { key: "individuals", label: "Individuals", count: individualTotal },
    { key: "companies", label: "Companies", count: companyTotal },
  ];

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#0f172a" }}>{CUSTOMER_TERM_PLURAL}</h2>
          <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 13 }}>Manage and view all your {CUSTOMER_TERM_PLURAL.toLowerCase()}</p>
        </div>
      </div>

      {/* Individuals | Companies */}
      <div role="tablist" aria-label="Customer type" style={{ display: "flex", gap: 6, marginBottom: 16, borderBottom: "1.5px solid #e2e8f0" }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              style={{
                padding: "10px 18px",
                border: "none",
                borderBottom: `3px solid ${active ? "#d01c4c" : "transparent"}`,
                marginBottom: -1.5,
                background: "none",
                fontSize: 13.5,
                fontWeight: active ? 800 : 600,
                color: active ? "#d01c4c" : "#64748b",
                cursor: "pointer",
              }}
            >
              {t.label}
              {t.count !== null && t.count !== undefined && (
                <span style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: active ? "#fdf2f5" : "#f1f5f9", color: active ? "#d01c4c" : "#64748b" }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search + filters */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 18, marginBottom: 18 }}>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder={
              onCompanies
                ? "Search by company name, TRN, mobile number or email..."
                : `Search by ${CUSTOMER_TERM.toLowerCase()} name or mobile number or email...`
            }
            aria-label={onCompanies ? "Search companies" : `Search ${CUSTOMER_TERM_PLURAL.toLowerCase()}`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: "100%", padding: "11px 40px 11px 16px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => setSearchValue("")}
              aria-label="Clear search"
              title="Clear search"
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "#94a3b8", fontSize: 18, lineHeight: 1, cursor: "pointer", padding: 4 }}
            >
              ×
            </button>
          )}
        </div>
        {!onCompanies && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => handleFilterChange(f.key)}
                style={{
                  padding: "6px 16px",
                  border: `1.5px solid ${filter === f.key ? "#d01c4c" : "#e2e8f0"}`,
                  borderRadius: 8,
                  fontSize: 12.5,
                  fontWeight: filter === f.key ? 700 : 500,
                  background: filter === f.key ? "#fff" : "#fff",
                  color: filter === f.key ? "#d01c4c" : "#64748b",
                  cursor: "pointer",
                }}
              >
                {f.key === "all" ? `All ${CUSTOMER_TERM_PLURAL}` : f.label}
              </button>
            ))}
          </div>

          {/* Sort by */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setSortOpen((v) => !v)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 12.5, fontWeight: 600, color: "#334155", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Sort by: {SORT_OPTIONS.find((s) => s.key === sortBy)?.label} ▾
            </button>
            {sortOpen && (
              <>
                <div onClick={() => setSortOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", zIndex: 11, minWidth: 150, overflow: "hidden" }}>
                  {SORT_OPTIONS.map((s) => (
                    <div
                      key={s.key}
                      onClick={() => handleSortChange(s.key)}
                      style={{
                        padding: "9px 14px",
                        fontSize: 12.5,
                        fontWeight: sortBy === s.key ? 700 : 500,
                        color: sortBy === s.key ? "#4f46e5" : "#334155",
                        background: sortBy === s.key ? "#eef2ff" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {s.label}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Companies: their own list (B2B bill-to customers) */}
      {onCompanies && <CompaniesTab search={companySearch} onTotal={setCompanyTotal} onOpen={openCompany} />}

      {/* Full-width customer list (individuals) */}
      {!onCompanies && (
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div
  style={{
    display: "grid",
    gridTemplateColumns: "2.6fr 1.6fr 1fr 1.2fr",
    padding: "12px 24px",
    background: "#f8fafc",
    borderBottom: "1px solid #f1f5f9",
  }}
>
  {[CUSTOMER_TERM.toUpperCase(), "LAST BOOKING", "BOOKING", "TOTAL SPENT"].map((h) => (
    <div
      key={h}
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#94a3b8",
        letterSpacing: 0.4,
      }}
    >
      {h}
    </div>
  ))}
</div>

        {loading ? (
          <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>Loading {CUSTOMER_TERM_PLURAL.toLowerCase()}…</div>
        ) : customers.length === 0 ? (
          <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No {CUSTOMER_TERM_PLURAL.toLowerCase()} found</div>
        ) : (
          customers.map((c, i) => {
            const status = c.is_active ? "Active" : "Inactive";
            const sc = STATUS_COLORS[status];
            return (
            <div
  key={`${c.record_type || "customer"}-${c.id}`}
  onClick={() => openCustomer(c)}
  style={{
    display: "grid",
    gridTemplateColumns: "2.6fr 1.6fr 1fr 1.2fr",
    padding: "14px 24px",
    alignItems: "center",
    borderBottom:
      i < customers.length - 1 ? "1px solid #f8fafc" : "none",
    cursor: "pointer",
    background: "#fff",
  }}
>
                <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#e0e7ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {initials(c.first_name, c.last_name)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fullName(c)}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{c.phone_number || "—"}</div>
                    <div style={{ fontSize: 11.5, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email || ""}</div>
                    {c.record_type === "dependent" && c.parent_name && (
                      <div style={{ fontSize: 11, color: "#d01c4c", fontWeight: 600, marginTop: 1 }}>Family member of {c.parent_name}</div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: "#475569" }}>{formatDateTime(c.last_booking_date, c.last_booking_time)}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{c.total_bookings}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{fmtMoney(c.total_spent)}</div>
                {/* <div>
                  <span style={{ padding: "3px 10px", borderRadius: 20, background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 700 }}>{status}</span>
                </div> */}
                {/* <div style={{ color: "#cbd5e1", fontSize: 18, textAlign: "right" }}>›</div> */}
              </div>
            );
          })
        )}

        {!loading && customers.length > 0 && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {pagination
                ? `Page ${page} of ${totalPages} · ${pagination.total} total ${CUSTOMER_TERM_PLURAL.toLowerCase()}`
                : `Showing ${customers.length} ${CUSTOMER_TERM_PLURAL.toLowerCase()}`}
            </span>
            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: page <= 1 ? "#f8fafc" : "#fff", color: page <= 1 ? "#cbd5e1" : "#334155", cursor: page <= 1 ? "default" : "pointer" }}
                >
                  ‹
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const p = start + idx;
                  if (p > totalPages) return null;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      style={{ padding: "6px 12px", border: `1.5px solid ${p === page ? "#4f46e5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13, fontWeight: p === page ? 700 : 500, background: p === page ? "#eef2ff" : "#fff", color: p === page ? "#4f46e5" : "#334155", cursor: "pointer", minWidth: 32 }}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: page >= totalPages ? "#f8fafc" : "#fff", color: page >= totalPages ? "#cbd5e1" : "#334155", cursor: page >= totalPages ? "default" : "pointer" }}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
